import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { faSignOutAlt, faCog } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion, Reorder } from "framer-motion";
import Logo from "../assets/icons/FINEPUNCHLOGO.png";
import { displayToast } from "../utils/Toast";
import ConfirmationModal from "./ConfirmationModal";
import Home from "../assets/svgIcons/Home.svg";
import { useErrorHandler } from "../services/ErrorHandler";
import { updateMenu } from "../apis/menu";
import { useMode } from "../context/ModeContext";
import { useListPageCache } from "../context/ListPageCacheContext";
import { getModuleParentRoute } from "../utils/moduleRoutes";
import "../styles/components/Sidebar.css";

const Sidebar = ({ setValidRoutes, onToggle, onLogout }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [isReordering, setIsReordering] = useState(false);
  const [initialOrder, setInitialOrder] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const navigate = useNavigate();
  const location = useLocation();
  const { handleErrorResponse } = useErrorHandler();
  const { mode } = useMode();
  const { clearCacheForTab } = useListPageCache();

  const isSidebarDisabled = mode === "add" || mode === "edit";

  const loadMenuItems = () => {
    try {
      const savedItems = localStorage.getItem("sidebarItems");

      const homeItem = {
        id: "home",
        label: "Home",
        path: "",
        iconUrl: Home,
        orderNumber: 0,
        isActive: true,
        status: "active",
        child_menu: [],
        organisation_id: localStorage.getItem("organisation_id") || null,
        parent_menu_id: null,
      };

      let activeItems = [homeItem];

      if (savedItems) {
        const parsedItems = JSON.parse(savedItems);

        const savedActiveItems = parsedItems
          .filter((item) => item.status === "active")
          .map((item) => ({
            id: item.id,
            label: item.display_title,
            path: item.name.toLowerCase().replace(/\s+/g, "-"),
            moduleRoute: getModuleParentRoute(item),
            iconUrl: item.icon_url,
            orderNumber: item.order_no,
            isActive: true,
            child_menu: item.child_menu,
            organisation_id: item.organisation_id,
            parent_menu_id: item.parent_menu_id,
            status: "active",
          }))
          .sort((a, b) => a.orderNumber - b.orderNumber);

        const homeExists = savedActiveItems.some((item) => item.path === "");

        if (!homeExists) {
          activeItems = [...activeItems, ...savedActiveItems];
        } else {
          activeItems = savedActiveItems;
        }
      }

      setMenuItems(activeItems);
      setValidRoutes(activeItems.map((item) => item.path));
    } catch (error) {
      console.error("Error loading menu items:", error);
    }
  };

  useEffect(() => {
    loadMenuItems();
  }, [setValidRoutes]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "sidebarItems") {
        loadMenuItems();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("sidebarItemsUpdated", loadMenuItems);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("sidebarItemsUpdated", loadMenuItems);
    };
  }, []);

  const handleDragStart = () => {
    setInitialOrder([...menuItems]);
    setIsReordering(true);
  };

  const handleDragEnd = async () => {
    if (!initialOrder) return;

    const orderChanged = menuItems.some((item, index) => {
      return item.id !== initialOrder[index].id;
    });

    if (!orderChanged) {
      setIsReordering(false);
      setInitialOrder(null);
      return;
    }

    try {
      const savedItems = localStorage.getItem("sidebarItems");
      const allItems = savedItems ? JSON.parse(savedItems) : [];

      const newOrderMap = new Map(
        menuItems.map((item, index) => [item.id, index + 1]),
      );

      const updatedMenuControls = allItems.map((item) => ({
        id: item.id,
        order_no: newOrderMap.has(item.id)
          ? newOrderMap.get(item.id)
          : item.order_no,
        status: item.status,
        organisation_id: item.organisation_id,
        parent_menu_id: item.parent_menu_id,
        child_menu: item.child_menu,
        display_title: item.display_title,
        name: item.name,
        icon_url: item.icon_url,
      }));

      const payload = {
        role_id: parseInt(localStorage.getItem("role_id"), 10),
        menu_controls: updatedMenuControls,
      };

      const data = await updateMenu(payload);
      if (data && data.user_menu_controls) {
        localStorage.setItem(
          "sidebarItems",
          JSON.stringify(data.user_menu_controls),
        );
        window.dispatchEvent(new Event("sidebarItemsUpdated"));
      }
      if (data?.description) {
        handleErrorResponse(data.description);
        displayToast(data.description);
      }
    } catch (error) {
      displayToast("Failed to update menu order. Please try again.", "error");
      setMenuItems(initialOrder);
    } finally {
      setIsReordering(false);
      setInitialOrder(null);
    }
  };

  const handleReorder = (newOrder) => {
    setMenuItems(newOrder);
  };

  const toggleSidebar = () => {
    const newMinimized = !isMinimized;
    setIsMinimized(newMinimized);
    onToggle(newMinimized);
  };

  const isSelected = (path) => {
    const currentPath = location.pathname.slice(1);
    if (path === "") {
      return currentPath === "";
    }
    return currentPath.startsWith(path);
  };

  return (
    <aside
      className={`sidebar ${isMinimized ? "minimized" : ""} ${
        isSidebarDisabled ? "sidebar-disabled" : ""
      }`}
    >
      <div className="sidebar-header">
        <Link to="/" className="sidebar-logo">
          {isMinimized || isMobile ? (
            <img
              src={Logo}
              alt="Finepunch Logo"
              className="sidebar-erp-logo"
            />
          ) : (
            "FINEPUNCH ERP"
          )}
        </Link>
        <button className="sidebar-toggle" onClick={toggleSidebar} aria-label={isMinimized ? "Expand sidebar" : "Collapse sidebar"} aria-expanded={!isMinimized}>
          {isMinimized ? ">>" : "<<"}
        </button>
      </div>

      <nav className="sidebar-nav">
        <Reorder.Group axis="y" values={menuItems} onReorder={handleReorder}>
          {menuItems.map((item) => (
            <Reorder.Item
              key={item.path}
              value={item}
              data-path={item.path}
              className={`sidebar-item ${
                isSelected(item.moduleRoute?.slice(1) || item.path) ? "selected" : ""
              } ${isSidebarDisabled ? "sidebar-item-disabled" : ""}`}
              onClick={() => {
                if (!isSidebarDisabled) {
                  const currentPath = location.pathname.slice(1);
                  const currentTab = currentPath.split("/")[0];
                  const targetTab = item.path || "";
                  if (currentTab && targetTab && currentTab !== targetTab) {
                    clearCacheForTab(currentTab);
                  }
                  navigate(item.moduleRoute || `/${item.path}`);
                }
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
              }}
              dragListener={!isReordering}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <motion.span
                className="sidebar-icon"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <img
                  src={item.iconUrl}
                  alt={`${item.label} icon`}
                  className="menu-icon"
                />
              </motion.span>
              {!isMinimized && (
                <motion.span
                  className="sidebar-label"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {item.label}
                </motion.span>
              )}
            </Reorder.Item>
          ))}
        </Reorder.Group>
      </nav>

      {(!isMobile || !isMinimized) && (
        <div className="sidebar-footer">
          <motion.div
            className={`sidebar-item ${
              isSelected("settings") ? "selected" : ""
            }`}
            onClick={() => navigate("/settings")}
            whileTap={{ scale: 0.95 }}
          >
            <motion.span className="sidebar-icon">
              <FontAwesomeIcon icon={faCog} />
            </motion.span>
            {!isMinimized && <span className="sidebar-label">Settings</span>}
          </motion.div>

          <motion.div
            className="sidebar-item"
            onClick={() => setShowLogoutConfirm(true)}
            whileTap={{ scale: 0.95 }}
          >
            <span className="sidebar-icon">
              <FontAwesomeIcon icon={faSignOutAlt} />
            </span>
            {!isMinimized && <span className="sidebar-label">Logout</span>}
          </motion.div>

          <ConfirmationModal
            isOpen={showLogoutConfirm}
            onClose={() => setShowLogoutConfirm(false)}
            onConfirm={() => {
              setShowLogoutConfirm(false);
              onLogout();
            }}
            message="Are you sure you want to logout?"
            confirmText="Logout"
            cancelText="Cancel"
          />
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
